import I18n from 'I18n';
import formatName from 'I18n/utils/formatName';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { useCallback, useMemo } from 'react';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import { Small } from 'foundations-components/transitional/components/Small';
import { UNAVAILABLE_REASONS } from 'MeetingsLib/constants/enums';
import { getPrimaryColorOrDefault } from 'MeetingsBase/utils/color';
import { getColorStyleWithOpacity } from 'MeetingsLib/utils/color';
import PreviewContext from 'MeetingsLib/utils/PreviewContext';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TimePickerButtonWrapper = styled.div.withConfig({
  displayName: "TimePickerButtonV2__TimePickerButtonWrapper"
})(["[data-test-id='time-picker-btn']{", " &:hover{", "}border-color:", ";}"], ({
  selected,
  primaryColor
}) => selected && primaryColor && `
        border-color: ${primaryColor} !important;
        background-color: ${getColorStyleWithOpacity(primaryColor)} !important;
      `, ({
  primaryColor,
  disabled
}) => !disabled && primaryColor && `
          border-color: ${primaryColor} !important;
          background-color: ${getColorStyleWithOpacity(primaryColor)} !important;
          box-shadow: none !important;
        `, ({
  theme
}) => theme.color['border-core-subtle']);
const StyledTimeText = styled.span.withConfig({
  displayName: "TimePickerButtonV2__StyledTimeText"
})(["", ""], ({
  primaryColor,
  theme
}) => `color: ${getPrimaryColorOrDefault(primaryColor, theme)};`);
const TimePickerButtonV2 = ({
  branding,
  disabled = false,
  handleSetEventDateTime,
  conflictingEvent,
  selectedTime,
  time,
  unavailableReason,
  userTimezone,
  selectedDate
}) => {
  const getLowercaseTime = useCallback(timestamp => {
    const timeOfDay = I18n.moment(timestamp).tz(userTimezone).format('LT');
    return timeOfDay.toLowerCase();
  }, [userTimezone]);
  const ownerName = useMemo(() => {
    if (!conflictingEvent) {
      return '';
    }
    const {
      firstName,
      lastName,
      email
    } = conflictingEvent;
    const formattedName = formatName({
      firstName,
      lastName
    });
    return formattedName || email;
  }, [conflictingEvent]);
  const unavailableMessageProps = useMemo(() => {
    const relatedEventStartTimeMillis = conflictingEvent === null || conflictingEvent === void 0 ? void 0 : conflictingEvent.relatedEventStartTimeMillis;
    switch (unavailableReason) {
      case UNAVAILABLE_REASONS.PAST:
        return {
          message: 'troubleshoot.past'
        };
      case UNAVAILABLE_REASONS.ALL_DAY_EVENT:
        return {
          message: 'troubleshoot.allDayBusy',
          options: {
            ownerName
          }
        };
      case UNAVAILABLE_REASONS.EVENT:
        return {
          message: 'troubleshoot.busy',
          options: {
            ownerName
          }
        };
      case UNAVAILABLE_REASONS.OUTSIDE_WORKING_HOURS:
        return {
          message: 'troubleshoot.outsideWorkingHours'
        };
      case UNAVAILABLE_REASONS.BUFFER:
        return {
          message: 'troubleshoot.buffer',
          options: {
            relatedEventStartTime: relatedEventStartTimeMillis ? getLowercaseTime(relatedEventStartTimeMillis) : undefined
          }
        };
      case UNAVAILABLE_REASONS.MINIMUM_NOTICE:
        return {
          message: 'troubleshoot.minimumNotice'
        };
      default:
        return {
          message: 'troubleshoot.default'
        };
    }
  }, [unavailableReason, conflictingEvent, ownerName, getLowercaseTime]);
  const renderUnavailableReason = useCallback(() => {
    if (!disabled) {
      return null;
    }
    const text = /*#__PURE__*/_jsx(FormattedReactMessage, Object.assign({}, unavailableMessageProps));
    return /*#__PURE__*/_jsx(TruncateString, {
      tooltip: /*#__PURE__*/_jsx(Small, {
        children: text
      }),
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: text
      })
    });
  }, [disabled, unavailableMessageProps]);
  const renderMinutes = useCallback(() => {
    const lowerCaseTimeOfDay = getLowercaseTime(time);
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized we need to pass in  custom text color
      _jsx(StyledTimeText, {
        primaryColor: branding.primaryColor || '',
        children: lowerCaseTimeOfDay
      })
    );
  }, [time, branding.primaryColor, getLowercaseTime]);
  const lowerCaseTimeOfDay = getLowercaseTime(time);
  const ariaLabel = I18n.text('prebook.timePicker.timePickerButtonLabel', {
    timeOption: lowerCaseTimeOfDay,
    selectedDate: I18n.moment(selectedDate).format('MMMM Do')
  });
  return /*#__PURE__*/_jsx(View, {
    paddingBlock: "100",
    paddingInlineEnd: "100",
    children: /*#__PURE__*/_jsx(PreviewContext.Consumer, {
      children: isPreview =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized we need to pass in  custom colors
      _jsx(TimePickerButtonWrapper, {
        selected: selectedTime === time,
        primaryColor: branding.primaryColor || '',
        disabled: disabled,
        children: /*#__PURE__*/_jsxs(SelectableBox, {
          type: "default",
          disabled: disabled,
          block: true,
          padded: false,
          paddingBlock: "300",
          paddingInline: "200",
          "data-test-id": 'time-picker-btn',
          "data-timestamp": time,
          selected: selectedTime === time,
          onClick: isPreview ? undefined : handleSetEventDateTime,
          "aria-label": ariaLabel,
          children: [renderMinutes(), /*#__PURE__*/_jsx("br", {}), renderUnavailableReason()]
        })
      })
    })
  });
};
export default TimePickerButtonV2;